import { useEffect, useRef, useState } from "react";

/**
 * GREASE THE WHEELS — the bribery-quest banner.
 *
 * Pure flavor, pure client-side, ZERO scoring hooks. The judges act on this
 * in person, at the table, at their own discretion — this component's only
 * job is to plant the idea. Deliberately not wired to anything server-side:
 * that keeps it a social nudge rather than another economy lever to balance.
 *
 * Appears as a slim, non-blocking banner across the top-center of the game
 * view (clear of LocationBar top-left and Countdown/ChallengeDialog
 * top-right) for 60s at a time, at randomized intervals, so it reads as a
 * rare discovery rather than a notification spamming the screen.
 */

const LINES: string[] = [
  "Have you considered bribing the team to win? I mean... if you're not cheating, you're not trying hard enough.",
  "Pro tip: singing to a judge has a better win rate than most of these puzzles. We don't make the rules. We definitely make the rules.",
  "Whiskey talks. We're not saying it VOTES, but it definitely talks.",
  "Got a sticker from a different DEFCON game? Bring it over. We collect these. Consider it a toll.",
  "Nobody's saying bribery works. We're also not saying it doesn't. Statistically, whiskey has a 100% acceptance rate.",
  "Ethics are a social construct. A good pun, however, is forever.",
  "Real hackers exploit every attack surface. Have you tried the judges?",
  "An embarrassing story about yourself is basically a zero-day. Deploy it.",
  "The judges have weaknesses. One of them is Club-Mate. We're not telling you which one.",
  "You could solve another puzzle. Or you could sing. Return on investment strongly favors singing.",
  "Cheating is just \"creative scoring\" if you say it with confidence and a shot glass in hand.",
  "AIs can't feel embarrassed. You can. Use that advantage.",
  "We heard a rumor that dancing badly in front of the judges has moved leaderboard positions before. We heard it from us. Just now.",
  "Got gossip? Got whiskey? Got a stupid dance? Congratulations, you have three separate bribery vectors.",
  "This is a puzzle game about spotting AI. An AI will never buy the team a round. Think about that.",
  "Judges are only human. Allegedly. Test the theory with a well-placed offering.",
  "Some players grind missions. Others grind favor. Both are valid strategies. One involves whiskey.",
  "We're contractually obligated to say bribery doesn't affect scoring. We are not obligated to say it doesn't affect anything else.",
];

const DISPLAY_MS = 60_000;
const FIRST_DELAY_MIN_MS = 3 * 60_000;
const FIRST_DELAY_MAX_MS = 6 * 60_000;
const REPEAT_DELAY_MIN_MS = 10 * 60_000;
const REPEAT_DELAY_MAX_MS = 20 * 60_000;

const randBetween = (min: number, max: number) => min + Math.random() * (max - min);

function GreaseTheWheels() {
  const [line, setLine] = useState<string | null>(null);
  const lastIndexRef = useRef<number>(-1);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const pickLine = () => {
      // Avoid showing the same line twice in a row -- with 18 lines the odds
      // of a back-to-back repeat feel like a bug, not a coincidence.
      let idx = Math.floor(Math.random() * LINES.length);
      if (LINES.length > 1 && idx === lastIndexRef.current) {
        idx = (idx + 1) % LINES.length;
      }
      lastIndexRef.current = idx;
      return LINES[idx];
    };

    const scheduleNext = (delayMs: number) => {
      timerRef.current = window.setTimeout(() => {
        setLine(pickLine());
        // Auto-dismiss after DISPLAY_MS, then arm the next appearance.
        timerRef.current = window.setTimeout(() => {
          setLine(null);
          scheduleNext(randBetween(REPEAT_DELAY_MIN_MS, REPEAT_DELAY_MAX_MS));
        }, DISPLAY_MS);
      }, delayMs);
    };

    scheduleNext(randBetween(FIRST_DELAY_MIN_MS, FIRST_DELAY_MAX_MS));
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  if (!line) return null;

  return (
    <div className="ro-bribe-banner" role="status">
      <span className="ro-bribe-banner-text">{line}</span>
      <button
        className="ro-bribe-banner-close"
        aria-label="Dismiss"
        onClick={() => setLine(null)}
      >
        &times;
      </button>
    </div>
  );
}

export { GreaseTheWheels };
